<template>
  <div class="onboarding-step profile-pic-step">
    <div class="step-header">
      <h3>{{ t('Foto de Perfil') }}</h3>
      <p class="step-description">{{ t('Adicione uma foto de perfil para o usuário (opcional)') }}</p>
    </div>

    <div class="upload-container">
      <!-- Image preview -->
      <div v-if="imagePreview" class="image-preview-container">
        <div class="image-preview">
          <img :src="imagePreview" :alt="t('Foto de perfil')" />
          <div class="image-overlay">
            <Button
              icon="pi pi-times"
              rounded
              text
              severity="danger"
              size="large"
              data-testid="users-onboarding-profile-clear"
              @click="clearImage"
            />
          </div>
        </div>
        <p class="image-name">{{ selectedFile?.name }}</p>
        <small class="image-size">{{ formatFileSize(selectedFile?.size) }}</small>
      </div>

      <!-- Upload area -->
      <div
        v-else
        class="upload-area"
        :class="{ 'drag-over': isDragOver }"
        @click="triggerFileInput"
        @drop.prevent="handleDrop"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
      >
        <i class="pi pi-cloud-upload upload-icon"></i>
        <h4>{{ t('Arraste uma imagem ou clique para selecionar') }}</h4>
        <p class="upload-help">{{ t('JPG, PNG ou GIF - Máximo 1MB') }}</p>

        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png,image/gif"
          style="display: none;"
          data-testid="users-onboarding-profile-file"
          @change="handleFileSelect"
        />
      </div>

      <!-- Error message -->
      <inline-message v-if="errorMessage" severity="error" :closable="true" @close="errorMessage = null">
        {{ errorMessage }}
      </inline-message>

      <!-- Skip option -->
      <div class="skip-option">
        <i class="pi pi-info-circle"></i>
        <p>{{ t('Você pode pular esta etapa e adicionar uma foto de perfil mais tarde') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';

const { t } = useI18n();

// Props
const props = withDefaults(defineProps<{
  modelValue?: File | null;
  username?: string;
}>(), {
  modelValue: null,
  username: ''
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: File | null];
}>();

// Local state
const fileInput = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const imagePreview = ref<string | null>(null);
const isDragOver = ref<boolean>(false);
const errorMessage = ref<string | null>(null);

// Constants
const MAX_FILE_SIZE = 1024 * 1024; // 1MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

// Methods
const triggerFileInput = (): void => {
  fileInput.value?.click();
};

const handleFileSelect = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    validateAndSetImage(file);
  }
};

const handleDrop = (event: DragEvent): void => {
  isDragOver.value = false;
  const file = event.dataTransfer?.files[0];
  if (file) {
    validateAndSetImage(file);
  }
};

const validateAndSetImage = (file: File): void => {
  errorMessage.value = null;

  // Validate file type
  if (!ALLOWED_TYPES.includes(file.type)) {
    errorMessage.value = t('Tipo de arquivo não permitido. Use JPG, PNG ou GIF.');
    log('OnboardingStep3ProfilePic', 'validateAndSetImage', 'Invalid file type', file.type);
    return;
  }

  // Validate file size
  if (file.size > MAX_FILE_SIZE) {
    errorMessage.value = t('Arquivo muito grande. Máximo permitido: 1MB');
    log('OnboardingStep3ProfilePic', 'validateAndSetImage', 'File too large', file.size);
    return;
  }

  // Set file and create preview
  selectedFile.value = file;
  createImagePreview(file);

  // Emit to parent
  emit('update:modelValue', file);

  log('OnboardingStep3ProfilePic', 'validateAndSetImage', 'Image selected', {
    name: file.name,
    size: file.size,
    type: file.type
  });
};

const createImagePreview = (file: File): void => {
  const reader = new FileReader();

  reader.onload = (e: ProgressEvent<FileReader>) => {
    imagePreview.value = (e.target?.result as string) ?? null;
  };

  reader.onerror = () => {
    errorMessage.value = t('Erro ao ler arquivo');
    log('OnboardingStep3ProfilePic', 'createImagePreview', 'Error reading file');
  };

  reader.readAsDataURL(file);
};

const clearImage = (): void => {
  selectedFile.value = null;
  imagePreview.value = null;
  errorMessage.value = null;

  if (fileInput.value) {
    fileInput.value.value = '';
  }

  emit('update:modelValue', null);

  log('OnboardingStep3ProfilePic', 'clearImage', 'Image cleared');
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';

  if (bytes < 1024) {
    return bytes + ' B';
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(1) + ' KB';
  } else {
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
};

// Watch for external changes (must be after function declarations)
watch(() => props.modelValue, (newValue) => {
  if (!newValue) {
    clearImage();
  }
}, { immediate: true });
</script>

<style scoped>
.onboarding-step {
  padding: 20px 0;
}

.step-header {
  margin-bottom: 24px;
  text-align: center;
}

.step-header h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin: 0;
  color: #666;
  font-size: 0.875rem;
}

.upload-container {
  max-width: 500px;
  margin: 0 auto;
}

/* Image preview */
.image-preview-container {
  text-align: center;
  margin-bottom: 24px;
}

.image-preview {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto 12px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #e5e7eb;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

.image-name {
  margin: 0 0 4px 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #333;
  word-break: break-word;
}

.image-size {
  color: #666;
  font-size: 0.8125rem;
}

/* Upload area */
.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 60px 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
}

.upload-area:hover {
  border-color: #6366f1;
  background: #f8f9ff;
}

.upload-area.drag-over {
  border-color: #6366f1;
  background: #f0f1ff;
  transform: scale(1.02);
}

.upload-icon {
  font-size: 3rem;
  color: #6366f1;
  margin-bottom: 16px;
}

.upload-area h4 {
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

.upload-help {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
}

/* Skip option */
.skip-option {
  margin-top: 24px;
  padding: 16px;
  background: #f0f9ff;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid #bae6fd;
}

.skip-option i {
  color: #0ea5e9;
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.skip-option p {
  margin: 0;
  font-size: 0.875rem;
  color: #333;
  line-height: 1.4;
}

/* Responsive */
@media (max-width: 768px) {
  .upload-area {
    padding: 40px 20px;
  }

  .image-preview {
    width: 150px;
    height: 150px;
  }
}
</style>
